import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Case } from '../case-info-page/case-info-page-models';
import { CaseBond, CaseCoDefendant, CaseCourtDate, CaseWitness, ChargeGroup } from '../case-info-page/models/case-info-models';
import { dateFromServerString, floatFromServerString, getAge, getAgeMessage, intFromServerString, normalizeServerString, stringDateToServerString } from '../../../core/utils/convertors';
import { CaseCoCounselor, UserDefineField } from '../../../shared/models/models';
import { CaseConfig, CaseConfigData, CaseConfigField, CaseConfigSection, CaseNote } from '../../../shared/models/civil-case.model';
import { CaseConfigTab } from 'src/app/shared/models/case-config-tab.model';

@Injectable({
  providedIn: 'root'
})
export class CaseApiService {

  private baseUrl: string;
  constructor(
    private http: HttpClient,
  ) {
    this.baseUrl = `${environment.api.url}WebServices`

  }

  public getCaseInfo(params: { caseId: string }) {
    const url = `${this.baseUrl}/CaseInfo.asmx/GetCaseInfoData`;

    return this.http
      .post<string>(url, {
        currentCaseId: params.caseId,
        defendentId: '',
      }, { params: { 'showLoader': true } })
      .pipe(
        map(wrapper => {
          const parsed = <
            {
              PageHeader: string;
              PaFile1: string;
              PaFile2: string;
              ReportNo: string | null;
              ReportNumberTooltip: string | null;
              Jurisdiction: string;
              JurisdictionCode: string;
              LeAgency: string | null;
              IsShowCauseNumber: boolean;
              CauseNumber: string | null;
              IsShowOcn: boolean;
              HeaderOcn: string | null;
              Ocn: string | null;
              OcnTooltip: string | null;
              Status: string;
              Stage: string | null;
              Division: string | null;
              AssignedName: string | null;
              AssignedEmail: string | null;
              AssignedEmailHref: string | null;
              DefenseAttyName: string | null;
              DefenseEmailAddress: string | null;
              DefenseEmailAddressHref: string | null;
              IsShowCaseWorker: boolean;
              IsShowBondInfo: boolean;
              CaseWorker: string | null;
              HasCharges: boolean;
              HasCoDefend: boolean;
              HasCourtDate: boolean;
              HasBond: boolean;
              HasDocument: boolean;
              btnCourtNumHRef: string | null;
              Phone: string | null;
              Address: string | null;
              CaseWorkerLabel: string | null;
              IsShowWitnessTab: boolean;
              HasWitness: boolean;
              IsShowBondTab: boolean;
              IsShowDocumentTab: boolean;
              IsShowChargeTab: boolean;
              IsShowCodefendantTab: boolean;
              IsShowCourtDateTab: boolean;
              IsDefaultCriminal: boolean;
              DocCategory: string;
            }
            >JSON.parse(wrapper);

          return <Case | null>{
            meta: {
              title: normalizeServerString(parsed.PageHeader),
              ocn: {
                show: parsed.IsShowOcn,
                label: parsed.HeaderOcn,
              },
              causeNumber: {
                show: parsed.IsShowCauseNumber,
              },
              caseWorker: {
                show: parsed.IsShowCaseWorker,
                label: parsed.CaseWorkerLabel
              },
              charges: {
                show: parsed.IsShowChargeTab,
                any: parsed.HasCharges,
              },
              coDefendants: {
                show: parsed.IsShowCodefendantTab,
                any: parsed.HasCoDefend,
              },
              courtDates: {
                show: parsed.IsShowCourtDateTab,
                any: parsed.HasCourtDate,
              },
              bonds: {
                show: parsed.IsShowBondTab,
                any: parsed.HasBond,
              },
              documents: {
                show: parsed.IsShowDocumentTab,
                any: parsed.HasDocument,
              },
              witness: {
                show: parsed.IsShowWitnessTab,
                any: parsed.HasWitness,
              }
            },
            fileNumber: {
              prefix: normalizeServerString(parsed.PaFile1),
              suffix: normalizeServerString(parsed.PaFile2),
            },
            report:
              !!parsed.ReportNo || !!parsed.ReportNumberTooltip
                ? {
                  number: normalizeServerString(parsed.ReportNo),
                  title: normalizeServerString(parsed.ReportNumberTooltip),
                }
                : null,
            leAgency: normalizeServerString(parsed.LeAgency),
            fingerprint:
              !!parsed.Ocn || !!parsed.OcnTooltip
                ? {
                  number: normalizeServerString(parsed.Ocn),
                  title: normalizeServerString(parsed.OcnTooltip),
                }
                : null,
            causeNumber: normalizeServerString(parsed.CauseNumber),
            status: normalizeServerString(parsed.Status),
            jurisdiction: {
              code: normalizeServerString(parsed.JurisdictionCode),
              description: normalizeServerString(parsed.Jurisdiction),
            },
            stage: normalizeServerString(parsed.Stage),
            division: normalizeServerString(parsed.Division),
            assigned: !!parsed.AssignedName
              ? {
                name: normalizeServerString(parsed.AssignedName),
                email: normalizeServerString(parsed.AssignedEmail),
                emailHref: normalizeServerString(parsed.AssignedEmailHref),
                phone: normalizeServerString(parsed.Phone),
                address: normalizeServerString(parsed.Address),
              }
              : null,
            defenseAttorney: !!parsed.DefenseAttyName
              ? {
                name: normalizeServerString(parsed.DefenseAttyName),
                email: normalizeServerString(parsed.DefenseEmailAddress),
                emailHref: normalizeServerString(parsed.DefenseEmailAddressHref),
              }
              : null,
            caseWorker: normalizeServerString(parsed.CaseWorker),
            btnCourtNumHRef: normalizeServerString(parsed.btnCourtNumHRef),
            isDefaultCriminal: parsed?.IsDefaultCriminal,
            docCategory: normalizeServerString(parsed.DocCategory)
          };
        })
      );
  }

  public getCaseCoCounsel(params: { caseId: string }) {
    const url = `${this.baseUrl}/CaseInfo.asmx/GetCoCounselList`;

    return this.http
      .post<string>(url, { fileNumber: params.caseId }, { params: { 'showLoader': true } })
      .pipe(
        map(wrapper =>
          (<{ UserName: string; Role: string; EmailAddress: string; InActive: boolean }[]>(
            JSON.parse(wrapper)
          )).map(
            parsed =>
              <CaseCoCounselor>{
                name: normalizeServerString(parsed.UserName),
                email: normalizeServerString(parsed.EmailAddress),
                role: normalizeServerString(parsed.Role),
                inActive: parsed.InActive,
              }
          )
        )
      );
  }

  public getCaseCharges(params: { caseId: string }) {
    const path = 'WebServices/CaseInfo.asmx/BindCaseInfoChargeGrid';

    return this.http
      .post<string>(
        environment.api.url + path,
        { currentCaseId: params.caseId },
        { params: { showLoader: true } }
      )
      .pipe(
        map(wrapper => {
          const parsedArray = JSON.parse(wrapper) as {
            ChargeDate: string,
            OffenseStreetAddress1: string,
            OffenseCity: string,
            OffenseState: string,
            OffenseZipCode: string,
            Charges: { Cnt: string, Charge: string, Type: string, Class: string, Disposition: string, Sentence: string, ChargeNcic: string }[]
          }[];

          return parsedArray.map(obj => ({
            date: obj.ChargeDate || '',
            formattedAddress: this.formatAddress(obj.OffenseStreetAddress1, '', obj.OffenseCity, obj.OffenseState, obj.OffenseZipCode),
            charges: obj.Charges.map(chg => ({
              cnt: chg.Cnt,
              chargeDescription: chg.Charge,
              type: chg.Type,
              class: chg.Class,
              disposition: chg.Disposition,
              sentence: chg.Sentence,
              enhancement: chg.ChargeNcic.replaceAll('&nbsp;', '')
            }))
          })) as ChargeGroup[];
        })
      );
  }

  public getCaseCoDefendants(params: { caseId: string }) {
    const path = 'WebServices/CaseInfo.asmx/GetCoDefForCaseInfo';

    return this.http
      .post<string>(environment.api.url + path, { currentCaseId: params.caseId }, { params: { 'showLoader': true } })
      .pipe(
        map(wrapper =>
          (<unknown[][]>JSON.parse(wrapper)).map(parsed => {
            const dobString = parsed[5] as string;
            const dob = new Date(dobString);
            const ageData = getAge(dob);
            const ageTooltip = getAgeMessage(ageData.years, ageData.months, ageData.days);

            return <CaseCoDefendant>{
              fileNumber: {
                prefix: normalizeServerString(parsed[0] as string),
                suffix: normalizeServerString(parsed[1] as string),
              },
              lastName: normalizeServerString(parsed[2] as string),
              firstName: normalizeServerString(parsed[3] as string),
              middleName: normalizeServerString(parsed[4] as string),
              dateOfBirth: stringDateToServerString(dobString),
              age: ageData.years.toString(),
              ageToolTip: ageTooltip,
              add1: normalizeServerString(parsed[6] as string),
              add2: normalizeServerString(parsed[7] as string),
              city: normalizeServerString(parsed[8] as string),
              state: normalizeServerString(parsed[9] as string),
              zip: normalizeServerString(parsed[10] as string),
              phone: normalizeServerString(parsed[11] as string),
              counts: normalizeServerString(parsed[12] as string),
              mugshotImage: parsed[13] as number[] | null
            };
          })
        )
      );
  }

  getCaseWitnessList(params: { caseId: string }) {
    const url = `${this.baseUrl}/CaseInfo.asmx/GetCaseWitnessList`;

    return this.http
      .post<string>(url, { fileNumber: params.caseId }, { params: { 'showLoader': true } })
      .pipe(
        map(wrapper =>
          (<unknown[][]>JSON.parse(wrapper)).map(
            parsed =>
              <CaseWitness>{
                name: normalizeServerString(parsed[1] as string),
                race: normalizeServerString(parsed[2] as string),
                sex: normalizeServerString(parsed[3] as string),
                dateOfBirth: normalizeServerString(parsed[4] as string),
                type: normalizeServerString(parsed[5] as string),
                defendantNumber: normalizeServerString(parsed[6] as string),
              }
          )
        )
      );
  }

  public getCaseCourtDates(params: { caseId: string }) {
    const path = 'WebServices/CaseInfo.asmx/BindCourtDateGrid';

    return this.http
      .post<string>(environment.api.url + path, { currentCaseId: params.caseId }, { params: { 'showLoader': true } })
      .pipe(
        map(wrapper =>
          (<unknown[][]>JSON.parse(wrapper)).map(
            parsed =>
              <CaseCourtDate>{
                docketCode: parsed[0] || '',
                description: parsed[1] || '',
                division: parsed[2] || '',
                eventDescription: parsed[3] || '',
                judgeName: parsed[4] || '',
                docketDate: parsed[5] || ''
              }
          )
        )
      );
  }

  public getCaseBonds(params: { caseId: string }) {
    const path = 'WebServices/CaseInfo.asmx/BindBondGrid';

    return this.http
      .post<string>(environment.api.url + path, { currentCaseId: params.caseId }, { params: { 'showLoader': true } })
      .pipe(
        map(wrapper =>
          (<unknown[][]>JSON.parse(wrapper)).map(
            parsed =>
              <CaseBond>{
                count: intFromServerString(parsed[0] as string),
                charge: normalizeServerString(parsed[1] as string),
                date: normalizeServerString(parsed[2] as string),
                amount: floatFromServerString(parsed[3] as string),
                type: normalizeServerString(parsed[4] as string),
                status: normalizeServerString(parsed[5] as string),
              }
          )
        )
      );
  }

  public getCivilCaseConfigs(params: { caseId: string }) {
    const url = `${this.baseUrl}/CaseInfo.asmx/GetCivilCaseFieldConfigs`;

    return this.http
      .post<string>(url, { currentCaseId: params.caseId }, { params: { 'showLoader': true } })
      .pipe(
        map(wrapper => {
          const parsed = <
            {
              TemplateTitle: string;
              TemplateId: number;
              Sections: { Id: string; ParentId: string; Width: string }[];
              Fields: {
                Id: number;
                SectionId: string;
                Label: string;
                Order: number;
                Width: number;
              }[];
              UserDefinedFields: {
                Id: number;
                SectionId: string;
                Label: string;
                Order: number;
                Width: number;
                FieldType: number;
                IsDisabled: boolean;
              }[];
              Tabs: { Identifier: string; Name: string }[];
            }
            >JSON.parse(wrapper);

          return <CaseConfig>{
            templateTitle: parsed.TemplateTitle,
            templateId: parsed.TemplateId,
            sections: parsed.Sections.map(
              section =>
                <CaseConfigSection>{
                  id: section.Id,
                  parentId: section.ParentId,
                  width: section.Width,
                }
            ),
            fields: parsed.Fields.map(
              field =>
                <CaseConfigField>{
                  id: field.Id,
                  sectionId: field.SectionId,
                  label: field.Label,
                  order: field.Order,
                  width: field.Width,
                }
            ),
            userDefinedFields: parsed.UserDefinedFields?.map(
              field =>
                <CaseConfigField>{
                  id: field.Id,
                  sectionId: field.SectionId,
                  label: field.Label,
                  order: field.Order,
                  isDisabled: field.IsDisabled,
                  type: field.FieldType,
                  width: field.Width,
                }
            ),
            tabs: parsed.Tabs.map(
              t =>
                <CaseConfigTab>{
                  identifier: normalizeServerString(t.Identifier),
                  name: normalizeServerString(t.Name),
                }
            ),
          };
        })
      );
  }

  public getCaseConfigControlsData(params: { caseId: string }) {
    const url = `${this.baseUrl}/CaseInfo.asmx/GetCaseConfigControlsData`;

    return this.http
      .post<string>(url, { fileNumber: params.caseId }, { params: { 'showLoader': true } })
      .pipe(
        map(wrapper => {
          const parsed = <
            {
              caseInfoData: {
                PaFile1: string;
                PaFile2: string;
                ReportNo: string;
                DefenseAttorney: string;
                ReportNumber: string;
                LeAgency: string;
                Ocn: string;
                LabNumber: string;
                ArrestNumber: string;
                AccidentFlag: string;
                AccidentNumber: string;
                ClockedSpeed: number;
                PostedSpeed: number;
                MphOver: string;
                VehicleMake: string;
                VehicleModel: string;
                VehicleVIN: string;
                VehicleYear: string;
                VehicleColor: string;
                VehicleLicense: string;
                VehicleState: string;
                VehicleLicExpYr: string;
                VehicleRegWeight: number;
                VehicleActWeight: number;
                AssignedName: string | null;
                AssignedEmail: string | null;
                AssignedEmailHref: string | null;
                FatalityFlag: boolean;
                LicenseSurrenderFlag: boolean;
                IgnitionInterlockFlag: boolean;
                ConstructionZoneFlag: boolean;
                SignWorkFlag: boolean;
                TravelSafeZoneFlag: boolean;
                DWIFlag: boolean;
                CommVehFlag: boolean;
                VehicleOperationFlag: boolean;
                DWITestAgency: string;
                DWITestType: string;
                BloodAlcoholContent: string;
                InstrumentNo: string;
                CertificationDate: string;
                CertificationDateString: string;
                SimulatorNumber: string;
                CivilMatter: string;
                CivilType: string;
                CivilNumber: string;
                CivilDescription: string;
                CivilClient: string;
                CivilClientDescription: string;
                CivilWork: string;
                CivilArea: string;
                CivilFunded: string;
                CivilCats: string;
                CivilDisposition: string;
                CivilExplanation: string;
                CaseCategory: string;
                CauseNumber: string;
                CaseWeight: number;
                ProSe: boolean;
                DefensePdRetainer: string;
                JurisdictionId: string;
                NeighborhoodCode: string;
                IsShowBondInfo: boolean;
                DsnSequence: string;
                PdNumber: string;
                IssuedDateValue: string;
                PoBadgeNumber: string;
                Status: string;
                Phone: string | null;
                IsShowBondTab: boolean;
                Address: string | null;
                BookingPd: string;
                SummonNum: string;
                SummonDate: string;
              };
              CaseChildHasValue: {
                TotalReportNumbers: number;
                TotalDefCoCounsel: number;
                TotalCaseNote: number;
              };
              DSN: string;
              DefenseAttyNameToolTip: string;
              Chk24HourHoldTimeDiff: string;
              HourlyHoldAmount: string;
              Hold24Hour: boolean;
              BookingPDToolTip: string;
              NeighborhoodToolTip: string;
              TicketNumber: string;
              CategoryToolTip: string;
              StageDescription: string;
              CaseWeightToolTip: string;
              BadgeNumberToolTip: string;
              OcnToolTip: string;
              JurisdictionCodeToolTip: string;
              CauseNoToolTip: string;
              ReportNoToolTip: string;
              LabNumberToolTip: string;
              DSNToolTip: string;
              ReferralDate: string;
              ReportReceivedDate: string;
              ArrestDate: string;
              ArrestTime: string;
              DocCategory: string;
              LEAgencyToolTip: string;
              DWITestAgencyTooltip: string;
            }
            >JSON.parse(wrapper);

          parsed.caseInfoData.ReportNo =
            parsed.caseInfoData.ReportNo || parsed.caseInfoData.ReportNumber;
          return <CaseConfigData>{
            fileNumber: {
              prefix: normalizeServerString(parsed.caseInfoData.PaFile1),
              suffix: normalizeServerString(parsed.caseInfoData.PaFile2),
            },
            assigned: !!parsed.caseInfoData.AssignedName
              ? {
                name: normalizeServerString(parsed.caseInfoData.AssignedName),
                email: normalizeServerString(parsed.caseInfoData.AssignedEmail),
                emailHref: normalizeServerString(parsed.caseInfoData.AssignedEmailHref),
                phone: normalizeServerString(parsed.caseInfoData.Phone),
                address: normalizeServerString(parsed.caseInfoData.Address),
              }
              : null,
            reportNo: {
              value: normalizeServerString(parsed.caseInfoData.ReportNo),
              tooltip: normalizeServerString(parsed.ReportNoToolTip),
            },
            reportNumber: normalizeServerString(parsed.caseInfoData.ReportNumber),
            referralDate: normalizeServerString(parsed.ReferralDate),
            reportReceivedDate: normalizeServerString(parsed.ReportReceivedDate),
            dsn: {
              dsnSequence: parsed.caseInfoData?.DsnSequence || '',
              value: !!parsed.caseInfoData?.DsnSequence
                ? normalizeServerString(`${parsed.DSN}:${parsed.caseInfoData.DsnSequence}`)
                : normalizeServerString(parsed.DSN),
              tooltip: normalizeServerString(parsed.DSNToolTip),
            },
            agency: {
              description: normalizeServerString(parsed.LEAgencyToolTip) || normalizeServerString(parsed.caseInfoData.PdNumber),
              value: normalizeServerString(parsed.caseInfoData.PdNumber) || ''
            },
            ocn: {
              value: normalizeServerString(parsed.caseInfoData.Ocn),
              tooltip: normalizeServerString(parsed.OcnToolTip),
            },
            labNumber: {
              value: normalizeServerString(parsed.caseInfoData.LabNumber),
              tooltip: normalizeServerString(parsed.LabNumberToolTip),
            },
            arrest: {
              number: normalizeServerString(parsed.caseInfoData.ArrestNumber),
              date: normalizeServerString(parsed.ArrestDate),
              time: normalizeServerString(parsed.ArrestTime),
            },
            hourHold: {
              value: parsed.Hold24Hour,
              timeDiff: normalizeServerString(parsed.Chk24HourHoldTimeDiff),
              amount: normalizeServerString(parsed.HourlyHoldAmount),
            },
            bookingPD: {
              description: normalizeServerString(parsed.BookingPDToolTip) || normalizeServerString(parsed.caseInfoData.BookingPd),
              value: normalizeServerString(parsed.caseInfoData.BookingPd)
            },
            neighborhood: {
              tooltip: normalizeServerString(parsed.NeighborhoodToolTip),
              code: normalizeServerString(parsed.caseInfoData.NeighborhoodCode),
            },
            ticketNumber: normalizeServerString(parsed.TicketNumber),
            accident: {
              flag: normalizeServerString(parsed.caseInfoData.AccidentFlag),
              number: normalizeServerString(parsed.caseInfoData.AccidentNumber),
            },
            clockedSpeed: parsed.caseInfoData.ClockedSpeed,
            postedSpeed: parsed.caseInfoData.PostedSpeed,
            mphOver: normalizeServerString(parsed.caseInfoData.MphOver),
            vehicle: {
              make: normalizeServerString(parsed.caseInfoData.VehicleMake),
              model: normalizeServerString(parsed.caseInfoData.VehicleModel),
              vin: normalizeServerString(parsed.caseInfoData.VehicleVIN),
              year: normalizeServerString(parsed.caseInfoData.VehicleYear),
              color: normalizeServerString(parsed.caseInfoData.VehicleColor),
              license: normalizeServerString(parsed.caseInfoData.VehicleLicense),
              state: normalizeServerString(parsed.caseInfoData.VehicleState),
              licenseExpirationYear: normalizeServerString(parsed.caseInfoData.VehicleLicExpYr),
              registrationWeight: parsed.caseInfoData.VehicleRegWeight,
              actualWeight: parsed.caseInfoData.VehicleActWeight,
            },
            isFatality: parsed.caseInfoData.FatalityFlag,
            isLicenseSurrender: parsed.caseInfoData.LicenseSurrenderFlag,
            isIgnitionInterlock: parsed.caseInfoData.IgnitionInterlockFlag,
            isConstructionZone: parsed.caseInfoData.ConstructionZoneFlag,
            isSignWork: parsed.caseInfoData.SignWorkFlag,
            isTravelSafeZone: parsed.caseInfoData.TravelSafeZoneFlag,
            isDWI: parsed.caseInfoData.DWIFlag,
            isVehicleOperation: parsed.caseInfoData.VehicleOperationFlag,
            isCommVeh: parsed.caseInfoData.CommVehFlag,
            dwiTest: {
              agency: normalizeServerString(
                parsed.caseInfoData.DWITestAgency || parsed.caseInfoData.PdNumber
              ),
              tooltip: normalizeServerString(parsed.DWITestAgencyTooltip || parsed.caseInfoData.DWITestAgency),
              type: normalizeServerString(parsed.caseInfoData.DWITestType),
            },
            bloodAlcoholContent: normalizeServerString(parsed.caseInfoData.BloodAlcoholContent),
            instrumentNo: normalizeServerString(parsed.caseInfoData.InstrumentNo),
            certificationDate: normalizeServerString(parsed.caseInfoData.CertificationDateString),
            simulatorNumber: normalizeServerString(parsed.caseInfoData.SimulatorNumber),
            causeNumber: {
              value: normalizeServerString(parsed.caseInfoData.CauseNumber),
              tooltip: normalizeServerString(parsed.CauseNoToolTip),
            },
            civil: {
              matter: normalizeServerString(parsed.caseInfoData.CivilMatter),
              type: normalizeServerString(parsed.caseInfoData.CivilType),
              number: normalizeServerString(parsed.caseInfoData.CivilNumber),
              description: normalizeServerString(parsed.caseInfoData.CivilDescription),
              client: normalizeServerString(parsed.caseInfoData.CivilClient),
              clientDescription: normalizeServerString(parsed.caseInfoData.CivilClientDescription),
              work: normalizeServerString(parsed.caseInfoData.CivilWork),
              area: normalizeServerString(parsed.caseInfoData.CivilArea),
              funded: normalizeServerString(parsed.caseInfoData.CivilFunded),
              cats: normalizeServerString(parsed.caseInfoData.CivilCats),
              disposition: normalizeServerString(parsed.caseInfoData.CivilDisposition),
              explanation: normalizeServerString(parsed.caseInfoData.CivilExplanation),
            },
            category: {
              value: normalizeServerString(parsed.caseInfoData.CaseCategory),
              tooltip: normalizeServerString(parsed.CategoryToolTip),
            },
            stageDescription: normalizeServerString(parsed.StageDescription),
            weight: {
              value: parsed.caseInfoData.CaseWeight,
              tooltip: normalizeServerString(parsed.CaseWeightToolTip),
            },
            badgeNumber: normalizeServerString(parsed.BadgeNumberToolTip),
            proSe: parsed.caseInfoData.ProSe,
            defense: {
              attorney: normalizeServerString(parsed.caseInfoData.DefenseAttorney),
              attorneyName: normalizeServerString(parsed.DefenseAttyNameToolTip),
              pdRetainer: normalizeServerString(parsed.caseInfoData.DefensePdRetainer),
            },
            jurisdiction: {
              id: normalizeServerString(parsed.caseInfoData.JurisdictionId),
              codeTooltip: normalizeServerString(parsed.JurisdictionCodeToolTip),
            },
            isShowBond: parsed.caseInfoData.IsShowBondTab,
            totals: {
              caseNote: parsed.CaseChildHasValue.TotalCaseNote,
              defCoCounsel: parsed.CaseChildHasValue.TotalDefCoCounsel,
              reportNumbers: parsed.CaseChildHasValue.TotalReportNumbers,
            },
            poBadgeNumber: normalizeServerString(parsed.caseInfoData?.PoBadgeNumber),
            courtNumber: normalizeServerString(parsed.caseInfoData?.CauseNumber),
            issuedDate: normalizeServerString(parsed.caseInfoData.IssuedDateValue),
            status: normalizeServerString(parsed.caseInfoData?.Status),
            docCategory: normalizeServerString(parsed?.DocCategory),
            summonDate: normalizeServerString(parsed.caseInfoData.SummonDate),
            summonNum: normalizeServerString(parsed.caseInfoData.SummonNum)
          };
        })
      );
  }

  public getUserDefineCaseConfigControlsData(params: { caseId: string }) {
    const url = `${this.baseUrl}/CaseInfo.asmx/GetUserDefineCaseConfigControlsData`;

    return this.http
      .post<string>(url, { fileNumber: params.caseId }, { params: { 'showLoader': true } })
      .pipe(
        map(wrapper =>
          (<{ FieldName: string; FieldValue: string }[]>JSON.parse(wrapper)).map(parsed => {
            return <UserDefineField>{
              name: normalizeServerString(parsed.FieldName),
              value: normalizeServerString(parsed.FieldValue),
            };
          })
        )
      );
  }

  public getReferralNotesByFileNumber(currentCaseId: string) {
    const url = `${this.baseUrl}/CaseInfo.asmx/GetReferralNotesByFileNumber`;
    return this.http.post<string>(url, { currentCaseId: currentCaseId }, { params: { showLoader: 'true' } });
  }

  public checkCaseAccessAuthorization(caseNo: string) {
    const url = `${this.baseUrl}/CaseInfo.asmx/CheckCaseAccessAuthorization`;

    return this.http.post<string>(url, { caseNo: caseNo });
  }

  private formatAddress(address1?: string | null, address2?: string | null, city?: string | null, state?: string | null, zipCode?: string | null): string {
    const parts = [
      address1,
      address2,
      [city, state].filter(Boolean).join(', '),
      zipCode,
    ];

    return parts.filter(p => !!p && p.trim()).join(' ');
  }
}
